import React, { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import rightimg from '../assets/images/Header/Arrow Right Up.svg'


const sanitize = (value) => (typeof value === "string" ? value.trim() : "");

const sanitizeImage = (image) => {
    if (!image) return null;
    const url = sanitize(image?.url || "");
    if (!url) return null;
    return { url, alt: sanitize(image?.alt || "") };
};

// Helper to check if URL is internal
// const isInternalUrl = (url) => {
//     if (!url || typeof url !== "string") return false;
//     const trimmed = url.trim();
//     if (trimmed.startsWith("/")) return true;
//     if (trimmed.startsWith("mailto:") || trimmed.startsWith("tel:")) return false;
//     try {
//         const urlObj = new URL(trimmed, window.location.origin);
//         return urlObj.origin === window.location.origin;
//     } catch {
//         return false;
//     }
// };
const isInternalUrl = (url) => {
    if (!url) return false;

    // Only treat relative paths as internal
    return url.startsWith("/");
};


const EventWrapperSection = ({ fields }) => {
    // React Hook must be called before any conditional returns
    const navigate = useNavigate();

    const sanitizedData = useMemo(() => {
        if (!fields) return null;

        const title = sanitize(fields.title || "");
        const text = sanitize(fields.text || "");
        const amenitiesTitle = sanitize(fields.amenities_title || "");
        const amenitiesSubtitle = sanitize(fields.amenities_subtitle || "");
        const button_name = sanitize(fields.button_name || "");
        const buttonUrl = sanitize(fields.button_url || "");

        const amenities = Array.isArray(fields.amenities)
            ? fields.amenities
                .map((item) => {
                    const text = sanitize(item?.text || "");
                    const url = sanitize(item?.url || "");
                    // Only require text, URL is optional
                    if (!text) return null;
                    return { text, url };
                })
                .filter(Boolean)
            : [];

        const image = sanitizeImage(fields.image);

        const stats = Array.isArray(fields.stats)
            ? fields.stats
                .map((stat) => {
                    const number = sanitize(stat?.number || "");
                    const label = sanitize(stat?.label || "");
                    if (!number || !label) return null;
                    return { number, label };
                })
                .filter(Boolean)
            : [];

        const hasContent = title || text || amenitiesTitle || amenitiesSubtitle || amenities.length > 0 || image || stats.length > 0 || button_name || buttonUrl;
        if (!hasContent) return null;

        return {
            title,
            text,
            amenitiesTitle,
            amenitiesSubtitle,
            amenities,
            image,
            stats,
            button_name,
            buttonUrl,
        };
    }, [fields]);

    if (!sanitizedData) return null;

    const { title, text, amenitiesTitle, amenitiesSubtitle, amenities, image, stats, button_name, buttonUrl } = sanitizedData;

    const ArrowIcon = () => (
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M0.810547 12.8102L12.9628 0.810181M12.9628 0.810181H3.8486M12.9628 0.810181V9.81018"
                stroke="#086493"
                strokeWidth="1.6203"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );

    return (
        <div className="event-sec">
            <div className="canvas">
                <section className="event-wrapper">
                    {/* LEFT CONTENT */}
                    {(title || text || amenitiesTitle || amenitiesSubtitle || amenities.length > 0) && (
                        <div className="event-content">
                            {title && <h2 className="common-title">{title}</h2>}

                            {text && (
                                <div className="event-text" dangerouslySetInnerHTML={{ __html: text }} />
                            )}

                            {(amenitiesTitle || amenitiesSubtitle || amenities.length > 0) && (
                                <div className="included-amenities">
                                    {amenitiesTitle && <h3>{amenitiesTitle}</h3>}
                                    {amenitiesSubtitle && <p className="opcity">{amenitiesSubtitle}</p>}

                                    {amenities.length > 0 && (
                                        <ul className="amenities">
                                            {amenities.map((amenity, index) => {
                                                // Check if URL exists and is not empty
                                                const url = amenity?.url || "";
                                                const hasUrl = url && url.trim() !== "";
                                                const isInternal = hasUrl && isInternalUrl(url);
                                                const text = amenity?.text || "";

                                                return (
                                                    <li key={`amenity-${index}`} className="opcity">
                                                        {hasUrl ? (
                                                            isInternal ? (
                                                                <Link to={url}>
                                                                    <span className="arrow-text">
                                                                        {text}{" "}
                                                                    </span>
                                                                    <span className="arrow-wrap">
                                                                        <span className="arrow-icon arrow-icon-1">
                                                                            <ArrowIcon />
                                                                        </span>
                                                                        <span className="arrow-icon arrow-icon-2">
                                                                            <ArrowIcon />
                                                                        </span>
                                                                    </span>
                                                                </Link>
                                                            ) : (
                                                                <a href={url} target="_blank" rel="noopener noreferrer">
                                                                    <span className="arrow-text">
                                                                        {text}{" "}
                                                                    </span>
                                                                    <span className="arrow-wrap">
                                                                        <span className="arrow-icon arrow-icon-1">
                                                                            <ArrowIcon />
                                                                        </span>
                                                                        <span className="arrow-icon arrow-icon-2">
                                                                            <ArrowIcon />
                                                                        </span>
                                                                    </span>
                                                                </a>
                                                            )
                                                        ) : (
                                                            <span>{text}</span>
                                                        )}
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    )}
                                </div>
                            )}

                            {button_name && buttonUrl && (
                                <button
                                    className="cta-btn aos-fade-up" data-stagger-index="2"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        if (isInternalUrl(buttonUrl)) {
                                            navigate(buttonUrl);
                                        } else {
                                            window.location.href = buttonUrl;
                                        }
                                    }}
                                >
                                    {button_name}
                                    <img src={rightimg} alt="Arrow icon" />
                                </button>
                            )}
                        </div>
                    )}

                    {/* RIGHT IMAGE */}
                    {image && (
                        <div className="event-image">
                            <img src={image.url} alt={image.alt || "Event"} />
                        </div>
                    )}

                    {/* STATS PANEL */}
                    {stats.length > 0 && (
                        <div className="stats-panel">
                            {stats.map((stat, index) => (
                                <div key={`stat-${index}`} className="stat">
                                    <span className="number">{stat.number}</span>
                                    <span className="subtext">{stat.label}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
};

export default EventWrapperSection;

